import config from 'config';

/**
 * Extracts a query parameter value from a URL string
 * @param {string} urlString - The URL to parse
 * @param {string} key - The parameter key to extract
 * @returns {string|null} The parameter value or null
 */
export function getQueryParam(urlString, key) {
  try {
    const url = new URL(urlString, window.location.origin);
    return url.searchParams.get(key);
  } catch (_e) {
    return null;
  }
}

/**
 * Adds or updates query parameters on a URL
 * @param {string} urlString - The base URL
 * @param {Object} params - Parameters to add/update
 * @returns {string} The updated URL
 */
export function withParams(urlString, params = {}) {
  try {
    const url = new URL(urlString, window.location.origin);
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') {
        url.searchParams.delete(k);
      } else {
        url.searchParams.set(k, String(v));
      }
    });
    return url.toString();
  } catch (_e) {
    // If malformed, return as-is
    return urlString;
  }
}

/**
 * Converts a path or URL to a full MMS URL
 * @param {string} pathOrUrl - The path or URL to convert
 * @returns {string} The full MMS URL
 */
export function toMmsUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  let path = pathOrUrl;

  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      const u = new URL(pathOrUrl);
      path = u.pathname + (u.search || '');
    } catch (_e) {
      path = pathOrUrl;
    }
  }

  const base =
    (config && config.images && config.images.mmsBaseUrl) ||
    'https://mms-images.staging.customink.com';
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Extracts hash from catalog image URL using consistent regex pattern
 * @param {string} imageUrl - The image URL to extract hash from
 * @returns {string|null} The extracted hash or null if not found
 */
export function extractCatalogHash(imageUrl) {
  if (!imageUrl) return null;
  const hashMatch = imageUrl.match(/\/catalog\/([a-f0-9]{32})\//);
  return hashMatch ? hashMatch[1] : null;
}

/**
 * Finds a category image URL by matching category ID
 * @param {Array} categoryImages - Array of category image objects
 * @param {string|number} primaryCategoryId - The category ID to match
 * @returns {string} The matching image URL or empty string
 */
export function findCategoryImageUrl(categoryImages, primaryCategoryId) {
  if (!Array.isArray(categoryImages)) return '';
  const match = categoryImages.find((img) => {
    const cid = img.category_id || img.primary_category_id || img.id;
    return String(cid) === String(primaryCategoryId);
  });
  return match?.image_url || '';
}
